import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

type Props = {
  tabs: string[]
  activeTab: string
  weldId: string
  weldFocusId: string
  isTabDisabled?: (tab: string) => boolean
  tabTitle?: (tab: string) => string | undefined
  onTabClick: (tab: string) => void
}

export function updateScrollFlags(
  el: HTMLElement | null
): { canScrollLeft: boolean; canScrollRight: boolean } {
  if (!el) return { canScrollLeft: false, canScrollRight: false }
  const maxScroll = el.scrollWidth - el.clientWidth
  return {
    canScrollLeft: el.scrollLeft > 1,
    canScrollRight: maxScroll - el.scrollLeft > 1
  }
}

/** Single-row Pass Record tabs with chevron scroll when labels overflow. */
export default function FloorSheetPassRecordTabStrip({
  tabs,
  activeTab,
  weldId,
  weldFocusId,
  isTabDisabled,
  tabTitle,
  onTabClick
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const refreshScrollFlags = useCallback(() => {
    const next = updateScrollFlags(scrollRef.current)
    setCanScrollLeft(next.canScrollLeft)
    setCanScrollRight(next.canScrollRight)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    refreshScrollFlags()
    el.addEventListener('scroll', refreshScrollFlags, { passive: true })
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(refreshScrollFlags) : null
    ro?.observe(el)
    window.addEventListener('resize', refreshScrollFlags)
    return () => {
      el.removeEventListener('scroll', refreshScrollFlags)
      ro?.disconnect()
      window.removeEventListener('resize', refreshScrollFlags)
    }
  }, [refreshScrollFlags, tabs])

  useEffect(() => {
    const node = activeRef.current
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ inline: 'nearest', block: 'nearest' })
    }
    refreshScrollFlags()
  }, [activeTab, refreshScrollFlags])

  const scrollByPage = (direction: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    const delta = Math.max(120, el.clientWidth * 0.6) * direction
    el.scrollBy({ left: delta, behavior: 'smooth' })
  }

  return (
    <div className="flex min-w-0 items-stretch gap-0.5" data-testid="floorsheet-pass-record-tab-strip">
      <button
        type="button"
        aria-label="Scroll tabs left"
        data-testid="floorsheet-tabs-scroll-left"
        disabled={!canScrollLeft}
        onClick={() => scrollByPage(-1)}
        className={`flex h-9 w-7 shrink-0 items-center justify-center rounded-md text-heritage-grey transition-colors hover:bg-page-bg hover:text-blueprint-blue disabled:pointer-events-none disabled:opacity-0 ${
          canScrollLeft ? '' : 'invisible'
        }`}
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden />
      </button>

      <div
        ref={scrollRef}
        className="min-w-0 flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex flex-nowrap items-center gap-2">
          {tabs.map((pass) => {
            const blocked = Boolean(isTabDisabled?.(pass))
            const isActive = activeTab === pass
            return (
              <button
                key={`${weldId}-${pass}`}
                ref={isActive ? activeRef : undefined}
                type="button"
                disabled={blocked}
                data-focus-field={`floorsheetTab.${pass}`}
                data-focus-weld-id={weldFocusId}
                title={tabTitle?.(pass)}
                onClick={() => onTabClick(pass)}
                className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  isActive
                    ? 'border-blueprint-blue text-blueprint-blue'
                    : 'border-transparent text-heritage-grey hover:text-blueprint-blue'
                }`}
              >
                {pass}
              </button>
            )
          })}
        </div>
      </div>

      <button
        type="button"
        aria-label="Scroll tabs right"
        data-testid="floorsheet-tabs-scroll-right"
        disabled={!canScrollRight}
        onClick={() => scrollByPage(1)}
        className={`flex h-9 w-7 shrink-0 items-center justify-center rounded-md text-heritage-grey transition-colors hover:bg-page-bg hover:text-blueprint-blue disabled:pointer-events-none disabled:opacity-0 ${
          canScrollRight ? '' : 'invisible'
        }`}
      >
        <ChevronRightIcon className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}
